import React, { memo, useEffect, useRef, useState } from 'react';
import {
  Control,
  FieldError,
  FieldValues,
  Path,
  UseFormRegister,
  useWatch,
} from 'react-hook-form';
import styled, { css } from 'styled-components';

import { ErrorMessage } from '@hookform/error-message';
import WarningIconSm from 'components/Icons/WarningIconSm';
import theme from 'theme/theme';

import { FOCUS_DELAY } from './Input';
import { ErrorText, FormLabel, InputContainer } from './StyledFormInputs';

type Props<TFormValues extends FieldValues> = {
  label: string;
  inputId: Path<TFormValues>;
  control: Control<TFormValues>;
  error?: FieldError;
  register: UseFormRegister<TFormValues>;
};

const PasscodeInput = <TFormValues extends Record<string, unknown>>({
  label,
  error,
  inputId,
  control,
  register,
}: Props<TFormValues>) => {
  const [isFocused, setIsFocus] = useState(false);
  const refEl = useRef<HTMLInputElement | null>(null);

  const { ref, ...rest } = register(inputId);

  const inputValue = useWatch({
    control,
    name: inputId,
  });

  useEffect(() => {
    if (refEl && refEl.current) {
      const focusDelay = setTimeout(() => {
        try {
          if (refEl?.current?.matches('*:-webkit-autofill')) {
            setIsFocus(true);
          }
          // eslint-disable-next-line no-empty
        } catch {}
      }, FOCUS_DELAY);

      return () => clearTimeout(focusDelay);
    }
  }, [inputId]);

  return (
    <InputContainer $error={!!error}>
      <FormLabel
        htmlFor={inputId}
        $error={!!error}
        $active={isFocused || !!inputValue || !!error}
      >
        {label}
      </FormLabel>
      <FormInput
        type="password"
        id={inputId}
        $error={!!error}
        {...rest}
        ref={(e) => {
          ref(e);
          refEl.current = e;
        }}
        onFocus={() => setIsFocus(true)}
      />

      <ErrorMessage
        name={inputId}
        errors={{ [inputId]: error }}
        render={({ message }) => (
          <ErrorText $error={!!error}>
            <WarningIconSm color={theme.colors.primary} /> {message}
          </ErrorText>
        )}
      />
    </InputContainer>
  );
};

const autofillInputField = css<{ $error?: boolean }>`
  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus,
  &:-internal-autofill-selected,
  input:-internal-autofill-selected,
  &:-webkit-autofill:active {
    /* stylelint-disable property-no-vendor-prefix, comment-empty-line-before */
    -webkit-text-fill-color: ${({ $error, theme }) =>
      $error ? theme.colors.lightRed : theme.colors.darkBlueV2};
    -webkit-transition-delay: 9999s;
    transition-delay: 9999s;
  }
`;

const FormInput = styled.input<{ $error?: boolean }>`
  width: 100%;
  height: 40px;
  margin-top: 11px;
  outline: none;
  border: none;
  background: none;
  box-sizing: border-box;
  font-style: normal;
  font-weight: normal;
  font-size: 20px;
  speak: none;
  -webkit-letter-spacing: 11px;
  -moz-letter-spacing: 11px;
  -ms-letter-spacing: 11px;
  letter-spacing: 11px;
  padding: 7px 0 0;
  color: ${({ $error, theme }) =>
    $error ? theme.colors.lightRed : theme.colors.darkBlueV2};
  ${autofillInputField}
`;

export default memo(PasscodeInput) as typeof PasscodeInput;
